import React, { useState, useEffect } from 'react';
import EditQuestionnaireQuestion from './Shards/Questions/EditQuestionnaireQuestion';
import AddQuestionnaireQuestion from './Shards/Questions/AddQuestionnaireQuestion';
import QuestionnaireQuestionCard from './Shards/Questions/QuestionnaireQuestionCard';
import ConfirmationPopup from '../Utils/ConfirmationPopup';
import { useSession } from '../Utils/SessionContext';
import { updateQuestionnaireQuestions } from './Utils/QuestionUpdater';
import toast from 'react-hot-toast';

const QuestionnaireQuestionList = ({ 
  questions, 
  onRegenerateQuestion, 
  showAnswers = true,
  questionnaireId = null,
  onQuestionUpdated = null
}) => {
  const { user } = useSession();
  
  // Local state for optimistic updates
  const [localQuestions, setLocalQuestions] = useState(questions);
  
  // Sync local state when props change
  useEffect(() => {
    setLocalQuestions(questions);
  }, [questions]);
  
  // State for edit modal
  const [editModalState, setEditModalState] = useState({
    isOpen: false,
    question: null,
    questionIndex: -1
  });

  // State for add modal
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  // State for delete confirmation
  const [deleteModalState, setDeleteModalState] = useState({
    show: false,
    question: null,
    questionIndex: -1
  });

  // Handle opening edit modal
  const handleEditQuestion = (question, index) => {
    setEditModalState({
      isOpen: true,
      question: question,
      questionIndex: index
    });
  };

  // Handle closing edit modal
  const handleCloseEditModal = () => {
    setEditModalState({
      isOpen: false,
      question: null,
      questionIndex: -1
    });
  };

  // Handle successful question update
  const handleQuestionUpdateSuccess = () => {
    if (onQuestionUpdated) {
      onQuestionUpdated();
    } else {
      // Default: reload page
      window.location.reload();
    }
  };

  // Handle opening delete confirmation
  const handleDeleteQuestion = (question, index) => {
    setDeleteModalState({
      show: true,
      question: question,
      questionIndex: index
    });
  };

  // Handle canceling delete
  const handleCancelDelete = () => {
    setDeleteModalState({
      show: false,
      question: null,
      questionIndex: -1
    });
  };

  // Handle confirmed delete
  const handleConfirmDelete = async () => {
    const { questionIndex } = deleteModalState;
    
    try {
      // Create updated questions array without the deleted question
      const updatedQuestions = questions.filter((_, i) => i !== questionIndex);
      
      // Use utility function to update questions
      await updateQuestionnaireQuestions(questionnaireId, updatedQuestions, user);

      toast.success('Question deleted successfully!');
      
      // Close modal
      handleCancelDelete();
      
      // Trigger refresh
      if (onQuestionUpdated) {
        onQuestionUpdated();
      } else {
        window.location.reload();
      }
    } catch (error) {
      console.error('Error deleting question:', error);
      toast.error('Failed to delete question. Please try again.');
    }
  };

  // Function to handle liking a question
  const handleLikeQuestion = (questionId) => {
    console.log(`Liking question with ID: ${questionId}`);
    // This would typically call an API to mark the question as liked
    alert(`Question ${questionId} liked! This is a placeholder - server request would happen here.`);
  };

  // Handle moving a question up with optimistic update
  const handleMoveUp = (index) => {
    if (index === 0) return; // Can't move first question up

    // Store original for potential rollback
    const originalQuestions = [...localQuestions];
    
    // 1. INSTANT UI UPDATE (optimistic)
    const updatedQuestions = [...localQuestions];
    [updatedQuestions[index - 1], updatedQuestions[index]] = 
      [updatedQuestions[index], updatedQuestions[index - 1]];
    
    setLocalQuestions(updatedQuestions); // Update UI immediately
    
    // 2. BACKGROUND SERVER UPDATE (no await!)
    updateQuestionnaireQuestions(questionnaireId, updatedQuestions, user)
      .then(() => {
        // Success - silent or subtle feedback
        console.log('Question moved up successfully');
      })
      .catch((error) => {
        // Error - rollback to original
        console.error('Error moving question up:', error);
        setLocalQuestions(originalQuestions); // Rollback
        toast.error('Failed to move question. Please try again.');
      });
  };

  // Handle moving a question down with optimistic update
  const handleMoveDown = (index) => {
    if (index === localQuestions.length - 1) return; // Can't move last question down

    // Store original for potential rollback
    const originalQuestions = [...localQuestions];
    
    // 1. INSTANT UI UPDATE (optimistic)
    const updatedQuestions = [...localQuestions];
    [updatedQuestions[index], updatedQuestions[index + 1]] = 
      [updatedQuestions[index + 1], updatedQuestions[index]];
    
    setLocalQuestions(updatedQuestions); // Update UI immediately
    
    // 2. BACKGROUND SERVER UPDATE (no await!)
    updateQuestionnaireQuestions(questionnaireId, updatedQuestions, user)
      .then(() => {
        // Success - silent or subtle feedback
        console.log('Question moved down successfully');
      })
      .catch((error) => {
        // Error - rollback to original
        console.error('Error moving question down:', error);
        setLocalQuestions(originalQuestions); // Rollback
        toast.error('Failed to move question. Please try again.');
      });
  };

  if (!questions || questions.length === 0) {
    return (
      <div className="p-4 bg-gray-50 rounded-lg">
        <p className="text-gray-600">No questions available.</p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4">
        {localQuestions.map((question, index) => (
          <QuestionnaireQuestionCard
            key={question.id}
            question={question}
            index={index}
            showAnswers={showAnswers}
            onRegenerateQuestion={onRegenerateQuestion}
            onLikeQuestion={handleLikeQuestion}
            onEditQuestion={handleEditQuestion}
            onDeleteQuestion={handleDeleteQuestion}
            onMoveUp={handleMoveUp}
            onMoveDown={handleMoveDown}
            totalQuestions={localQuestions.length}
            questionnaireId={questionnaireId}
          />
        ))}
      </div>
      
      {/* Add Questionnaire Question Modal */}
      <AddQuestionnaireQuestion
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        questionnaireId={questionnaireId}
        allQuestions={localQuestions}
        onSuccess={handleQuestionUpdateSuccess}
      />

      {/* Edit Questionnaire Question Modal */}
      <EditQuestionnaireQuestion
        isOpen={editModalState.isOpen}
        onClose={handleCloseEditModal}
        question={editModalState.question}
        questionIndex={editModalState.questionIndex}
        questionnaireId={questionnaireId}
        allQuestions={localQuestions}
        onSuccess={handleQuestionUpdateSuccess}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationPopup
        show={deleteModalState.show}
        title="Delete Question"
        message={`Are you sure you want to delete question #${deleteModalState.questionIndex + 1}? This action cannot be undone.`}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </>
  );
};

export default QuestionnaireQuestionList;
